import type { IDecimalValueAndUnit } from '@/interfaces/IDecimalValueAndUnit'
import { MESSAGE_CONSTANTS, REGEX } from '../constants'
import {
  GREATER_THAN_OR_EQUALS_PREVIOUS_RULE,
  GREATER_THAN_PREVIOUS_RULE
} from '../constants/message-strings'
import { containsAllValues } from './app-helpers'
import { mergeValueAndUnitObject } from './string-helpers'
import { computed } from 'vue'
import { ValueBasedTriggerType } from '@/enum/ValueBasedTriggerType'
import { AssetType } from '@/enum'

export const requiredRule = (value: any, message?: string) =>
  (value !== null &&
    value !== undefined &&
    (typeof value !== 'string' || value.trim() !== '') &&
    (!Array.isArray(value) || value.length > 0)) ||
  (message ?? MESSAGE_CONSTANTS.REQUIRED_RULE)
export const numberRule = (value: string) =>
  !value ? true : REGEX.NUMBER_REGEX.test(value) || MESSAGE_CONSTANTS.NUMBER_RULE
export const positiveNumberRule = (value: string) =>
  !value ? true : REGEX.POSITIVE_NUMBER_REGEX.test(value) || MESSAGE_CONSTANTS.NUMBER_POSITIVE_RULE
export const integerRule = (value: string) =>
  !value ? true : REGEX.POSITIVE_INTEGER_REGEX.test(value) || MESSAGE_CONSTANTS.INTEGER_RULE
export const phoneNumberRule = (value: string) => {
  if (!value) return true
  const sanitizedValue = value.charAt(0) === '+' ? value.slice(1) : value
  return REGEX.POSITIVE_INTEGER_REGEX.test(sanitizedValue) || MESSAGE_CONSTANTS.PHONE_NUMBER_RULE
}
export const maximumDecimalPlacesRule = (value: string) => {
  if (!value) return true
  return (
    REGEX.MAXIMUM_DECIMAL_PLACES_REGEX.test(value) || MESSAGE_CONSTANTS.MAXIMUM_DECIMAL_PLACES_RULE
  )
}
export const minimumNumberRule = (value: string | number, minValue: number, message: string) => {
  if (!value) return true
  return Number(value) >= minValue || message
}

export const emailRules = [
  (currentemail: string) => !!currentemail || MESSAGE_CONSTANTS.EMAIL_VALIDATION_REQUIRED_RULE,
  (currentemail: string) =>
    REGEX.EMAIL_REGEX.test(currentemail) || MESSAGE_CONSTANTS.EMAIL_VALIDATION_FORMAT_RULE
]

export const passwordRules = [
  (value: string) => !!value || MESSAGE_CONSTANTS.PASSWORD_VALIDATION_REQUIRED_RULE,
  (value: string) => value.length >= 8 || MESSAGE_CONSTANTS.PASSWORD_REQUIREMENTS[0],
  (value: string) =>
    REGEX.ONE_UPPERCASE_REGEX.test(value) || MESSAGE_CONSTANTS.PASSWORD_REQUIREMENTS[1],
  (value: string) =>
    REGEX.ONE_LOWERCASE_REGEX.test(value) || MESSAGE_CONSTANTS.PASSWORD_REQUIREMENTS[2],
  (value: string) =>
    REGEX.ONE_NUMBER_REGEX.test(value) || MESSAGE_CONSTANTS.PASSWORD_REQUIREMENTS[3],
  (value: string) =>
    REGEX.ONE_SPECIAL_CHARACTER_REGEX.test(value) || MESSAGE_CONSTANTS.PASSWORD_REQUIREMENTS[4]
]

export const urlRule = (value: string) =>
  !value ? true : REGEX.URL_REGEX.test(value) || MESSAGE_CONSTANTS.URL_VALIDATION_FORMAT_RULE

export const greaterThanPreviousOdometerReadingRule = (odometerReading: IDecimalValueAndUnit) => {
  return (value: string) => {
    if (!containsAllValues(odometerReading)) return true
    return (
      Number(value) > odometerReading.value ||
      `${GREATER_THAN_PREVIOUS_RULE} ${mergeValueAndUnitObject(odometerReading)}`
    )
  }
}

export const greaterThanCurrentOdometerReadingRule = (odometerReading?: IDecimalValueAndUnit) => {
  return (value: string | number) => {
    const numericValue = Number(value)
    if (!odometerReading || odometerReading.value == null) return true
    if (value === null || value === undefined || value === '') return true
    return (
      numericValue > odometerReading.value ||
      `Value must be greater than ${mergeValueAndUnitObject(odometerReading)}`
    )
  }
}

export const greaterThanOrEqualPreviousOdometerReadingRule = (
  odometerReading: IDecimalValueAndUnit
) => {
  return (value: string) => {
    if (!containsAllValues(odometerReading)) return true
    return (
      Number(value) >= odometerReading.value ||
      `${GREATER_THAN_OR_EQUALS_PREVIOUS_RULE} ${mergeValueAndUnitObject(odometerReading)}`
    )
  }
}

export const greaterThanOrEqualToPreviousMachineHourEntryRule = (machineHours?: number) => {
  return (value: string) => {
    if (!machineHours) return true
    return (
      Number(value) >= machineHours || `${GREATER_THAN_OR_EQUALS_PREVIOUS_RULE} ${machineHours}`
    )
  }
}

export const greaterThanPreviousMachineHourEntryRule = (machineHours?: number) => {
  return (value: string | number) => {
    if (machineHours == null) return true
    if (value === null || value === undefined || value === '') return true
    return Number(value) > machineHours || `${GREATER_THAN_PREVIOUS_RULE} ${machineHours}`
  }
}

export const greaterThanCurrentMachineHoursRule = (currentMachineHours?: number) => {
  return (value: string | number) => {
    if (value === '' || value === null || value === undefined) return true

    const numericValue = Number(value)
    if (isNaN(numericValue)) return true
    if (currentMachineHours === null || currentMachineHours === undefined) return true

    return numericValue > currentMachineHours || `Value must be greater than ${currentMachineHours}`
  }
}


export const greaterThanCurrentOdomoterReadingRule = (currentOdometerReading?: number) => {
  return (value: number) => {
    const numericValue = Number(value)
    if (isNaN(numericValue)) return true
    if (!value || currentOdometerReading === undefined || currentOdometerReading === null)
      return true
    return value > currentOdometerReading || `Value must be greater than ${currentOdometerReading}`
  }
}
export const machineHoursRule = (
  triggerType: ValueBasedTriggerType,
  currentMachineHours?: number,
  assetType?: AssetType,
  isChecked?: boolean
): ((value: any) => true | string)[] => {
  if (!isChecked) return []

  return [
    (value: string | number) => {
      const numericValue = Number(value)

      if (value === '' || value === null || value === undefined || isNaN(numericValue)) {
        return true // Let empty fields pass (optional)
      }

      const shouldCheckAgainstCurrent =
        triggerType === ValueBasedTriggerType.AtValue &&
        [AssetType.MACHINE, AssetType.VEHICLE, AssetType.EQUIPMENT].includes(assetType!)

      return shouldCheckAgainstCurrent
        ? numericValue > (currentMachineHours ?? 0) || `Value must be greater than ${currentMachineHours}`
        : numericValue > 0 || 'Value must be greater than zero'
    }
  ]
};

export const odometerRules = (
  triggerType: ValueBasedTriggerType,
  currentOdometerReading?: IDecimalValueAndUnit,
  assetType?: AssetType,
  isChecked?: boolean
): ((value: any) => true | string)[] => {
  if (!isChecked) return []

  const isVehicleOrMachine = [AssetType.MACHINE, AssetType.VEHICLE].includes(assetType!)
  if (!isVehicleOrMachine) return []

  return [
    (value: string | number) => {
      const numericValue = Number(value)

      if (value === '' || value === null || value === undefined || isNaN(numericValue)) {
        return true // Let empty fields pass
      }

      if (triggerType === ValueBasedTriggerType.AtValue && currentOdometerReading?.value != null) {
        return numericValue > currentOdometerReading.value || `Value must be greater than ${currentOdometerReading.value}`
      }

      return numericValue > 0 || 'Value must be greater than zero'
    }
  ]
};



export const greaterThanZeroRuleOdometer = (
  assetType?: AssetType,
  isChecked?: boolean
) => {
  if (!isChecked) return []

  if (assetType === AssetType.MACHINE || assetType === AssetType.VEHICLE) {
    return [greaterThanZeroRule()]
  } else {
    return []
  }
}
export const greaterThanZeroRuleMachineHours = (
  assetType?: AssetType,
  isChecked?: boolean
) => {
  if (!isChecked) return []

  if (assetType === AssetType.MACHINE || assetType === AssetType.VEHICLE || assetType === AssetType.EQUIPMENT) {
    return [greaterThanZeroRule()]
  } else {
    return []
  }
}

export const greaterThanZeroRule = () => {
  return (value: string | number) => {
    const numericValue = Number(value)
    if (isNaN(numericValue)) return true
    return Number(value) > 0 || MESSAGE_CONSTANTS.GREATER_THAN_ZERO_RULE
  }
}
